import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ChevronLeft, ChevronRight, Lock, Lightbulb, FlaskConical, Flame, Star, Trophy, Network, Layers, Sparkles, Award, FileText, CheckCircle, AlertCircle, Clock3 } from "lucide-react";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { fetchProfile } from "../service/ProfileService";
import { useTheme } from "./ThemeContext";

const FEED_TYPE_META = {
    HIGH_NOVELTY:        { Icon: Lightbulb,    accent: "var(--accent)", verb: "earned a novelty score of",      tail: "on", text: "var(--accent-text)" },
    HIGH_METHOD:         { Icon: FlaskConical, accent: "#5b8fa8", verb: "earned a methodology score of",  tail: "on", text: "var(--series-blue)" },
    HIGH_IMPACT:         { Icon: Flame,        accent: "#a86b3c", verb: "earned an impact score of",       tail: "on", text: "var(--bronze)" },
    STRONG_OVERALL:      { Icon: Star,         accent: "#c89c5a", verb: "scored",                          tail: "overall on" },
    PERSONAL_BEST:       { Icon: Trophy,       accent: "#c89c5a", verb: "set a new personal best of",      tail: "with" },
    DEEP_THINKERS_MATCHED:{ Icon: Network,     accent: "#7e5fb8", verb: "matched with",                    tail: "deep thinkers on", valueSuffix: " new", darkText: "#a98bdc" },
    SIMILAR_PAPERS_FOUND:{ Icon: Layers,       accent: "#3d8a9a", verb: "matched with",                    tail: "similar papers on", darkText: "#6fb8c6" },
    FIRST_PAPER:         { Icon: Sparkles,     accent: "#c89c5a", verb: "analyzed their first paper:",     hideValue: true },
    MILESTONE_PAPERS:    { Icon: Award,        accent: "#c89c5a", verb: "analyzed their", tail: "paper:",   isOrdinal: true },
    FEATURED_THIS_WEEK:  { Icon: Sparkles,     accent: "var(--accent)", verb: "is featured as Paper of the Week:", hideValue: true },
};

function capitalize(s) {
    if (!s) return s;
    return s.charAt(0).toUpperCase() + s.slice(1);
}

function ordinal(n) {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function pageWindows(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const show = new Set([1, total, current - 1, current, current + 1].filter(n => n >= 1 && n <= total));
    const sorted = [...show].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
        result.push(sorted[i]);
    }
    return result;
}

const SUBMISSIONS_PAGE_SIZE = 5;

function formatRelative(ts) {
    if (!ts) return "";
    const diff = Date.now() - ts;
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
    return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function StatusBadge({ status }) {
    if (!status) return null;
    const s = status.toLowerCase();
    const isComplete = s === "completed";
    const isError = s === "error";
    const cls = isComplete ? "badge-complete" : isError ? "badge-error" : "badge-processing";
    const Icon = isComplete ? CheckCircle : isError ? AlertCircle : Clock3;
    const label = isComplete ? "Complete" : isError ? "Error" : "In Process";
    return (
        <span className={`status-badge ${cls}`}>
            <Icon size={12} />
            {label}
        </span>
    );
}

function overall(m) {
    if (m.noveltyScore == null || m.methodScore == null || m.impactScore == null) return null;
    return Math.round((m.noveltyScore + m.methodScore + m.impactScore) / 3);
}

function Avatar({ imageUrl, name, size = 88 }) {
    const initials = (name || "")
        .split(" ")
        .map(p => p[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase() || "·";
    if (imageUrl) {
        return (
            <div className="profile-avatar" style={{ width: size, height: size }}>
                <img src={imageUrl} alt="" />
            </div>
        );
    }
    return (
        <div className="profile-avatar" style={{ width: size, height: size, fontSize: size * 0.32 }}>
            {initials}
        </div>
    );
}

function FeedEntry({ event }) {
    const { theme } = useTheme();
    const meta = FEED_TYPE_META[event.type] || FEED_TYPE_META.STRONG_OVERALL;
    const { Icon, accent, hideValue, isOrdinal, valueSuffix } = meta;
    const valueColor = meta.text || (theme === "dark" && meta.darkText) || accent;
    const value = !hideValue && (event.score != null ? event.score : event.count);
    return (
        <li className="profile-feed-item">
            <span className="profile-feed-icon" style={{ background: accent }}>
                <Icon size={12} color="white" />
            </span>
            <div className="profile-feed-body">
                <p className="profile-feed-text">
                    {isOrdinal && event.count != null ? (
                        <>{capitalize(meta.verb)} <strong>{ordinal(event.count)}</strong> {meta.tail} <span className="profile-feed-target">{event.manuscriptTitle}</span></>
                    ) : value != null ? (
                        <>
                            {capitalize(meta.verb)}{" "}
                            <strong style={{ color: valueColor }}>{value}{valueSuffix || ""}</strong>
                            {meta.tail ? ` ${meta.tail} ` : " "}
                            <span className="profile-feed-target">{event.manuscriptTitle}</span>
                        </>
                    ) : (
                        <>{capitalize(meta.verb)} <span className="profile-feed-target">{event.manuscriptTitle}</span></>
                    )}
                </p>
                <p className="profile-feed-time">{formatRelative(event.createdDate)}</p>
            </div>
        </li>
    );
}

export default function Profile() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const userId = params.get("id");

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [subPage, setSubPage] = useState(1);

    useEffect(() => {
        if (!userId) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        fetchProfile(userId)
            .then(res => { if (!cancelled) setProfile(res); })
            .catch(() => { if (!cancelled) setProfile(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [userId]);

    const renderBody = () => {
        if (loading) return <div className="profile-state">Loading profile…</div>;
        if (!profile || profile.notFound) return <div className="profile-state">We couldn't find that researcher.</div>;

        if (!profile.isPublic) {
            const name = profile.user?.displayName ?? "This researcher";
            return (
                <div className="profile-private">
                    <div className="profile-private-icon"><Lock size={28} /></div>
                    <h2 className="profile-private-title">{name}'s profile is private</h2>
                    <p className="profile-private-sub">This researcher hasn't made their profile public. Only they can see their submissions and activity.</p>
                </div>
            );
        }

        const { user, stats, scoreHistory, submissions, feedEvents } = profile;
        const memberSince = user?.memberSince
            ? new Date(user.memberSince).toLocaleDateString("en-US", { month: "long", year: "numeric" })
            : null;
        const chartData = (scoreHistory || []).map(p => ({
            name: p.scoredDate ? new Date(p.scoredDate).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "",
            Novelty: p.novelty ?? 0,
            Methodology: p.method ?? 0,
            Impact: p.impact ?? 0,
        }));

        return (
            <>
                <section className="profile-hero">
                    <div className="profile-hero-aurora" />
                    <div className="profile-hero-row">
                        <Avatar imageUrl={user?.imageUrl} name={user?.displayName} />
                        <div className="profile-hero-meta">
                            <h1 className="profile-hero-name">{user?.displayName}</h1>
                            {memberSince && <p className="profile-hero-since">Researcher since {memberSince}</p>}
                        </div>
                    </div>
                    <div className="profile-stats-grid">
                        <div className="profile-stat">
                            <p className="profile-stat-label">Submissions</p>
                            <p className="profile-stat-value">{stats?.totalSubmissions ?? "—"}</p>
                        </div>
                        <div className="profile-stat">
                            <p className="profile-stat-label">Analyzed</p>
                            <p className="profile-stat-value">{stats?.papersAnalyzed ?? "—"}</p>
                        </div>
                        <div className="profile-stat">
                            <p className="profile-stat-label">Avg Score</p>
                            <p className="profile-stat-value">{stats?.averageScore ?? "—"}</p>
                        </div>
                        <div className="profile-stat">
                            <p className="profile-stat-label">Top Score</p>
                            <p className="profile-stat-value">{stats?.topScore ?? "—"}</p>
                        </div>
                    </div>
                </section>

                {chartData.length > 0 && (
                    <section className="score-history-card profile-section">
                        <div className="score-history-header">
                            <div>
                                <p className="score-history-title">Score History</p>
                                <p className="score-history-sub">Recent novelty, methodology, and impact</p>
                            </div>
                            <div className="score-history-legend">
                                <span className="legend-dot" style={{ background: "var(--accent-text)" }} />Novelty
                                <span className="legend-dot" style={{ background: "var(--series-blue)" }} />Methodology
                                <span className="legend-dot" style={{ background: "var(--bronze)" }} />Impact
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={chartData} margin={{ top: 8, right: 16, left: -20, bottom: 0 }}>
                                <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" vertical={false} />
                                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                <YAxis domain={[50, 100]} tick={{ fontSize: 11, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--ink-900)" }} cursor={{ stroke: "var(--border)" }} />
                                <Line type="monotone" dataKey="Novelty" stroke="var(--accent-text)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                                <Line type="monotone" dataKey="Methodology" stroke="var(--series-blue)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                                <Line type="monotone" dataKey="Impact" stroke="var(--bronze)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </section>
                )}

                <div className="profile-columns">
                    {(() => {
                        const list = submissions || [];
                        const totalPages = Math.max(1, Math.ceil(list.length / SUBMISSIONS_PAGE_SIZE));
                        const safePage = Math.min(subPage, totalPages);
                        const start = (safePage - 1) * SUBMISSIONS_PAGE_SIZE;
                        const visible = list.slice(start, start + SUBMISSIONS_PAGE_SIZE);
                        return (
                            <section className="profile-submissions-section profile-section">
                                <div className="profile-submissions-header">Submissions</div>
                                {list.length === 0 ? (
                                    <div className="profile-empty">No submissions yet.</div>
                                ) : (
                                    <>
                                        {totalPages > 1 && (
                                            <div className="pagination-bar" style={{ marginBottom: 8 }}>
                                                <p className="pagination-count">{start + 1}–{Math.min(start + SUBMISSIONS_PAGE_SIZE, list.length)} of {list.length}</p>
                                                <div className="pagination-controls">
                                                    <button onClick={() => setSubPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === 1 ? "var(--surface-3)" : "var(--surface)", cursor: safePage === 1 ? "default" : "pointer", fontSize: 12, color: safePage === 1 ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                                                        <ChevronLeft size={13} />
                                                        <span className="pagination-btn-text">Previous</span>
                                                    </button>
                                                    <div className="pagination-pages">
                                                        {pageWindows(safePage, totalPages).map((n, i) =>
                                                            n === null
                                                                ? <span key={`e${i}`} style={{ padding: "5px 2px", fontSize: 12, color: "var(--ink-300)" }}>…</span>
                                                                : <button key={n} onClick={() => setSubPage(n)} style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid var(--border)", background: n === safePage ? "var(--brand)" : "var(--surface)", color: n === safePage ? "white" : "var(--ink-700)", cursor: "pointer", fontSize: 12, fontWeight: n === safePage ? 600 : 400 }}>{n}</button>
                                                        )}
                                                    </div>
                                                    <button onClick={() => setSubPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === totalPages ? "var(--surface-3)" : "var(--surface)", cursor: safePage === totalPages ? "default" : "pointer", fontSize: 12, color: safePage === totalPages ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                                                        <span className="pagination-btn-text">Next</span>
                                                        <ChevronRight size={13} />
                                                    </button>
                                                </div>
                                                <div className="pagination-spacer" />
                                            </div>
                                        )}

                                        <div className="sub-list">
                                            {visible.map(item => {
                                                const score = overall(item);
                                                const title = (item.title?.trim()) || "Untitled";
                                                const authors = (typeof item.authors === "string" && item.authors.trim()) || "Unknown Authors";
                                                const isComplete = item.status?.toLowerCase() === "completed";
                                                return (
                                                    <div key={item.id} className="sub-card" onClick={() => navigate(`/profile/submission?user=${user.id}&id=${item.id}`)} style={{ cursor: "pointer" }}>
                                                        <div className="sub-icon">
                                                            <FileText size={15} />
                                                        </div>
                                                        <div className="sub-info">
                                                            <p className="sub-title">{title}</p>
                                                            <p className="sub-authors">{authors}</p>
                                                            {isComplete && (
                                                                <div className="sub-scores-row">
                                                                    {item.noveltyScore != null && <span className="sub-score-chip sub-score-chip-n">N: {item.noveltyScore}</span>}
                                                                    {item.methodScore != null && <span className="sub-score-chip sub-score-chip-m">M: {item.methodScore}</span>}
                                                                    {item.impactScore != null && <span className="sub-score-chip sub-score-chip-i">I: {item.impactScore}</span>}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="sub-meta">
                                                            <p className="sub-date">{item.uploadDate ? new Date(item.uploadDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}</p>
                                                            <div className="sub-right">
                                                                <StatusBadge status={item.status} />
                                                                {isComplete && score != null && score > 0 && <span className="sub-score">{score}</span>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </section>
                        );
                    })()}

                    <section className="profile-feed-card profile-section">
                        <p className="profile-feed-title">Recent Wins</p>
                        {(!feedEvents || feedEvents.length === 0) ? (
                            <div className="profile-empty">No celebrated moments yet.</div>
                        ) : (
                            <ul className="profile-feed-list">
                                {feedEvents.map(e => <FeedEntry key={e.id} event={e} />)}
                            </ul>
                        )}
                    </section>
                </div>
            </>
        );
    };

    return (
        <div className="home-layout">
            <Sidebar activePage="" />
            <main className="home-main">
                <Header />
                <div className="profile-page">
                    <button className="profile-back" onClick={() => navigate(-1)}>
                        <ChevronLeft size={15} />
                        Back
                    </button>
                    {renderBody()}
                </div>
            </main>
        </div>
    );
}
